import React, { memo, useMemo, useState, useEffect, useCallback, useRef } from "react";
import {
  TextField,
  Autocomplete,
  Grid,
  Typography,
  Table,
  TableRow,
  TableCell,
  TableContainer,
  TableHead,
  TableBody,
  Paper,
  Box,
  Button,
  IconButton,
  Link
} from '@mui/material';
import CommentIcon from '@mui/icons-material/Comment';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DeleteIcon from '@mui/icons-material/Delete';

const GrupoAnalitos = ({
  groupKey,
  group,
  equipoOptions,
  metodoOptions,
  comentarioVisible,
  comentariosResultado,
  handleResultadoChange,
  handleComentarioChange,
  toggleComentarioVisible,
  handleComentarioResultadoChange,
  selectedTopEquipo,           // Solo para lectura del filtro general
  handleEquipoAnalitoChange,
  selectedEquipo = "",      // Equipo seleccionado del padre
  selectedMetodo = "",      // Método seleccionado del padre
  onEquipoChange,              // Callback para cambios de equipo
  onMetodoChange,              // Callback para cambios de método
  localResultados = {},        // Estados locales optimizados para resultados
  localComentarios = {},       // Estados locales optimizados para comentarios
  onDocumentUpload,           // Callback para subir documentos
  onDocumentDelete,           // Callback para eliminar documentos
  user,                       // Usuario actual para la subida
  // 🎯 NUEVAS PROPS PARA NAVEGACIÓN GLOBAL
  globalInputRefs,            // Referencias globales de inputs
  globalKeyDownHandler,       // Manejador global de teclado
  globalResultadoFieldIds     // Lista global de IDs de campos
}) => {
  const [selectedIndividualEquipo, setSelectedIndividualEquipo] = useState(""); // Estado independiente para el filtro individual
  const [manuallySelected, setManuallySelected] = useState(false); // Para rastrear si fue selección manual
  
  // Referencias para navegación con teclado entre campos de resultados
  const inputRefs = useRef({});
  const resultadoFieldIds = useRef([]);
  const autocompleteOpenState = useRef({}); // Rastrear si el autocomplete está abierto
  
  // OPTIMIZACIÓN CRÍTICA: Estado local para digitación fluida
  const [localValues, setLocalValues] = useState({});
  
  // Referencias para debouncing
  const updateTimers = useRef({});

  // OPTIMIZACIÓN CRÍTICA: Función mejorada para obtener valores
  const getLocalValue = useCallback((key) => {
    // Primero buscar en estado local (digitación actual)
    if (localValues[key] !== undefined) {
      return localValues[key];
    }
    
    // Luego buscar en props del padre (valor guardado)
    if (key.startsWith('resultado-')) {
      return localResultados[key] || '';
    } else if (key.startsWith('comentario-')) {
      return localComentarios[key] || '';
    }
    return '';
  }, [localValues, localResultados, localComentarios]);

  // Función optimizada para manejar cambios locales - DIGITACIÓN FLUIDA
    const handleLocalChange = useCallback((key, value) => {
    if (DEBUG_PERFORMANCE) {
      console.log(`[PERF-DEBUG] 🎯 handleLocalChange - Key: ${key}, Value: "${value}"`);
    }
    // ✅ PASO 1: Actualizar estado local INMEDIATAMENTE para digitación fluida
    setLocalValues(prev => ({
      ...prev,
      [key]: value
    }));
    
    // ✅ PASO 2: Cancelar timer anterior si existe
    if (updateTimers.current[key]) {
      clearTimeout(updateTimers.current[key]);
    }
    
    // ✅ PASO 3: Programar actualización al padre después de 1.5 segundos de inactividad
    updateTimers.current[key] = setTimeout(() => {
      if (DEBUG_PERFORMANCE) {
        console.log(`[PERF-DEBUG] ⏰ Timer ejecutado - Guardando ${key}: "${value}"`);
      }
      
      const [type, idMuestra, id] = key.split('-');
      if (type === 'resultado') {
        handleResultadoChange(parseInt(idMuestra), parseInt(id), value);
      } else if (type === 'comentario') {
        handleComentarioChange(parseInt(idMuestra), parseInt(id), value);
      }
      
      // ⚠️ NO limpiar el estado local inmediatamente
      // El estado local se limpiará cuando el padre confirme que tiene el valor
      // Esto evita que el campo se vacíe antes de que el servidor responda
    }, 1500); // 1.5 segundos de debounce
  }, [handleResultadoChange, handleComentarioChange]);

  // Cleanup - limpiar timers al desmontar
  useEffect(() => {
    return () => {
      // Cancelar todos los timers pendientes
      Object.values(updateTimers.current).forEach(timer => {
        if (timer) clearTimeout(timer);
      });
    };
  }, []);

  // Sincronizar estado local con props del padre cuando cambien
  useEffect(() => {
    // ✅ Limpiar valores locales SOLO cuando el padre confirme que tiene el valor
    // Esto evita que el campo se vacíe antes de que el servidor responda al POST
    setLocalValues(prev => {
      const newValues = { ...prev };
      let hasChanges = false;
      
      Object.keys(newValues).forEach(key => {
        const localValue = newValues[key];
        
        if (key.startsWith('resultado-')) {
          // Solo limpiar si el padre tiene EXACTAMENTE el mismo valor
          const parentValue = localResultados[key];
          if (parentValue !== undefined && parentValue === localValue) {
            delete newValues[key];
            hasChanges = true;
            if (DEBUG_PERFORMANCE) {
              console.log(`[PERF-DEBUG] 🧹 Limpiando valor local de resultado ${key} - Padre confirmó guardado`);
            }
          }
        } else if (key.startsWith('comentario-')) {
          // Solo limpiar si el padre tiene EXACTAMENTE el mismo valor
          const parentValue = localComentarios[key];
          if (parentValue !== undefined && parentValue === localValue) {
            delete newValues[key];
            hasChanges = true;
            if (DEBUG_PERFORMANCE) {
              console.log(`[PERF-DEBUG] 🧹 Limpiando valor local de comentario ${key} - Padre confirmó guardado`);
            }
          }
        }
      });
      
      return hasChanges ? newValues : prev;
    });
  }, [localResultados, localComentarios]);

  // Performance optimization: Logs solo para debugging específico de lentitud
  const DEBUG_PERFORMANCE = false; // Cambiar a true solo para debuggear lentitud específica
  
  // Mensaje informativo para el desarrollador
  React.useEffect(() => {
    if (DEBUG_PERFORMANCE) {
      console.log("🚀 [PERF-DEBUG] Modo debugging de performance ACTIVADO en GrupoAnalitos.jsx");
      console.log("💡 Para desactivar logs y mejorar performance, cambiar DEBUG_PERFORMANCE a false");
    }
  }, []);
  
  // 🔥 LOG CRÍTICO - Detectar re-renders del componente
  if (DEBUG_PERFORMANCE) {
    console.log(`🏷️ [PERF-DEBUG] ⚡ RENDER GrupoAnalitos [${groupKey}] - Filtro General: ${selectedTopEquipo}, Método: ${selectedMetodo}, Equipo: ${selectedEquipo}`);
    console.log(`[PERF-DEBUG] 📊 Grupo tiene ${group.length} analitos`);
  }

  const esRemitido = group.length > 0 && group[0].remitido === true;

  const equipoFilterOptions = useMemo(
    () => {
      // Obtener equipos únicos que están realmente asignados a este grupo específico
      const equiposEnGrupo = new Set();
      group.forEach(detalle => {
        if (detalle.idEquipo) {
          equiposEnGrupo.add(detalle.idEquipo.toString());
        } else {
          equiposEnGrupo.add("SIN_EQUIPO");
        }
      });

      // Filtrar equipoOptions para mostrar solo los equipos que están en este grupo
      const equiposDisponiblesEnGrupo = equipoOptions.filter(eq => 
        equiposEnGrupo.has(eq.id.toString())
      );

      // Solo loggear si estamos debuggeando performance
      if (DEBUG_PERFORMANCE) {
        console.log(`🎯 [PERF-DEBUG] Equipos en grupo [${groupKey}]:`, Array.from(equiposEnGrupo));
        console.log(`⚙️ [PERF-DEBUG] Equipos disponibles para grupo [${groupKey}]:`, equiposDisponiblesEnGrupo);
      }

      return [
        ...(equiposDisponiblesEnGrupo.length > 1 ? [{ label: "Todos", value: "ALL" }] : []),
        ...equiposDisponiblesEnGrupo.map(eq => ({ 
          label: `${eq.descripcion}${eq.id === "SIN_EQUIPO" ? "" : ` (ID: ${eq.id})`}`, 
          value: eq.id 
        }))
      ];
    },
    [equipoOptions, group, groupKey]
  );

  const metodoFilterOptions = useMemo(
    () => metodoOptions.map(m => ({ label: m.descripcion, value: m.id })),
    [metodoOptions]
  );

  // 🎯 Actualizar lista de IDs de campos de resultado cuando cambia el grupo filtrado
  useEffect(() => {
    const ids = group
      .filter(detalle => !detalle.remitido) // Solo campos editables
      .map(detalle => `resultado-${detalle.idMuestra}-${detalle.id}`);
    resultadoFieldIds.current = ids;
    
    if (DEBUG_PERFORMANCE) {
      console.log(`🎯 [${groupKey}] Lista local actualizada: ${ids.length} campos [${ids.join(', ')}]`);
    }
  }, [group, groupKey]);

  // 🎯 Manejador de navegación con teclado (TAB, SHIFT+TAB, Flechas)
  const handleKeyDown = useCallback((e, currentId, isAutocompleteOpen) => {
    if (DEBUG_PERFORMANCE) {
      console.log(`🎯 [${groupKey}] handleKeyDown: ${e.key}, campo: ${currentId}, autocomplete: ${isAutocompleteOpen}, tieneGlobal: ${!!globalKeyDownHandler}`);
    }
    
    // 🎯 NAVEGACIÓN GLOBAL: Si tenemos el manejador global y el autocomplete está cerrado, delegamos
    if (globalKeyDownHandler && !isAutocompleteOpen) {
      if (DEBUG_PERFORMANCE) {
        console.log(`🎯 [${groupKey}] Delegando a navegación global`);
      }
      globalKeyDownHandler(e, currentId, isAutocompleteOpen);
      return;
    }
    
    if (DEBUG_PERFORMANCE) {
      console.log(`🎯 [${groupKey}] Usando navegación local. Campos locales: ${resultadoFieldIds.current.length}`);
    }
    
    // 🎯 NAVEGACIÓN LOCAL: Solo manejar localmente si no hay sistema global o el autocomplete está abierto
    const currentIndex = resultadoFieldIds.current.indexOf(currentId);
    
    if (currentIndex === -1) return;

    let targetIndex = -1;

    // TAB: ir al siguiente campo (siempre funciona)
    if (e.key === 'Tab' && !e.shiftKey) {
      e.preventDefault();
      targetIndex = currentIndex + 1;
      if (targetIndex >= resultadoFieldIds.current.length) {
        targetIndex = 0; // Volver al primero
      }
    }
    // SHIFT + TAB: ir al campo anterior (siempre funciona)
    else if (e.key === 'Tab' && e.shiftKey) {
      e.preventDefault();
      targetIndex = currentIndex - 1;
      if (targetIndex < 0) {
        targetIndex = resultadoFieldIds.current.length - 1; // Ir al último
      }
    }
    // FLECHA ABAJO: ir al siguiente campo (SOLO si el autocomplete está cerrado)
    else if (e.key === 'ArrowDown' && !isAutocompleteOpen) {
      e.preventDefault();
      targetIndex = currentIndex + 1;
      if (targetIndex >= resultadoFieldIds.current.length) {
        targetIndex = 0;
      }
    }
    // FLECHA ARRIBA: ir al campo anterior (SOLO si el autocomplete está cerrado)
    else if (e.key === 'ArrowUp' && !isAutocompleteOpen) {
      e.preventDefault();
      targetIndex = currentIndex - 1;
      if (targetIndex < 0) {
        targetIndex = resultadoFieldIds.current.length - 1;
      }
    }

    // Enfocar el campo objetivo
    if (targetIndex !== -1) {
      const targetId = resultadoFieldIds.current[targetIndex];
      const targetInput = inputRefs.current[targetId];
      
      if (targetInput) {
        // Usar setTimeout para asegurar que el foco se aplique correctamente
        setTimeout(() => {
          targetInput.focus();
          // Seleccionar todo el texto para facilitar el reemplazo
          if (targetInput.select) {
            targetInput.select();
          }
        }, 0);
      }
    }
  }, [globalKeyDownHandler]);

  // Filtro inteligente optimizado - logs solo para debugging específico
  const filteredGroup = useMemo(() => {
    // Performance debugging solo cuando es necesario
    if (DEBUG_PERFORMANCE) {
      console.log(`🔍 [PERF-DEBUG] Aplicando filtro individual en grupo [${groupKey}] - Equipo: ${selectedIndividualEquipo}`);
      console.time(`filteredGroup-${groupKey}`);
    }
    
    let baseFiltered = group;

    // Solo filtrar por equipo cuando hay uno seleccionado específicamente
    if (selectedIndividualEquipo !== "" && selectedIndividualEquipo !== "ALL") {
      if (selectedIndividualEquipo === "SIN_EQUIPO") {
        // Filtrar solo analitos sin equipo
        baseFiltered = group.filter(detalle => !detalle.idEquipo);
      } else {
        // CORRECCIÓN: Filtrar SOLAMENTE por el equipo específico seleccionado
        // No incluir analitos con resultados de otros equipos para evitar descripciones incorrectas
        baseFiltered = group.filter(detalle => {
          const equipoCoincide = detalle.idEquipo?.toString() === selectedIndividualEquipo.toString();
          return equipoCoincide;
        });
      }

      // Si no hay coincidencias para el equipo específico, no mostrar nada 
      // (esto evita mostrar analitos de otros equipos con descripciones incorrectas)
      if (baseFiltered.length === 0) {
        return [];
      }
    }

    // Ya no deduplicamos por idAnalito - permitimos que el mismo analito aparezca múltiples veces
    // si viene en la lista porque pertenece a diferentes tipos de análisis o muestras
    
    // Ordenamos directamente la lista filtrada por posición
    // Solo mostrar logs de ordenamiento si estamos debuggeando performance
    if (DEBUG_PERFORMANCE) {
      console.log("[PERF-DEBUG] Posiciones ANTES de ordenar:");
      baseFiltered.forEach(item => {
        console.log(`Analito: ${item.analito}, Posición: ${item.posicion || "NO DEFINIDA"}, TipoAnalisis: ${item.descripcionTipoAnalisis}, Muestra: ${item.descripcionMuestra}`);
      });
    }
    
    // Ordenamos por posición
    const resultado = baseFiltered.sort((a, b) => {
      // Convertimos a número y aseguramos que undefined/null sea 0
      const posicionA = parseInt(a.posicion) || 0;
      const posicionB = parseInt(b.posicion) || 0;
      
      // Ordenar solo por posición
      return posicionA - posicionB;
    });
    
    // Solo loggear resultado final si estamos debuggeando performance
    if (DEBUG_PERFORMANCE) {
      console.log(`✅ [PERF-DEBUG] Resultado final del filtro: ${resultado.length} analitos para el grupo [${groupKey}]`);
      resultado.forEach(r => console.log(`   - ${r.analito} (Equipo: ${r.idEquipo || 'Sin equipo'}) - Posición: ${r.posicion || 0} - ID Equipo Real: ${r.idEquipo}`));
      console.timeEnd(`filteredGroup-${groupKey}`);
    }
    return resultado;
  }, [group, selectedIndividualEquipo, groupKey]);

  // Preselección inteligente de equipo mejorada - logs solo para debugging específico
  useEffect(() => {
    // Solo loggear si estamos debuggeando performance
    if (DEBUG_PERFORMANCE) {
      console.log(`🔄 [PERF-DEBUG] UseEffect preselección individual [${groupKey}] - Inicializando configuración`);
    }

    // Aplicar preselección automática SOLO si:
    // 1. NO fue una selección manual
    // 2. Está en "ALL" (estado inicial)
    if (selectedIndividualEquipo === "" && !manuallySelected) {
      if (DEBUG_PERFORMANCE) {
        console.log(`🎯 [PERF-DEBUG] Analizando preselección AUTOMÁTICA individual para grupo [${groupKey}]`);
      }
      
      // 1. PRIORIDAD MÁXIMA: Buscar analito con resultado existente y equipo asignado
      const detalleConResultado = group.find(detalle =>
        detalle.resultado && detalle.resultado.toString().trim() !== "" && detalle.idEquipo
      );

      if (detalleConResultado && detalleConResultado.idEquipo) {
        if (DEBUG_PERFORMANCE) {
          console.log(`✅ [PERF-DEBUG] Auto-seleccionando equipo con resultado existente: ${detalleConResultado.idEquipo} para [${groupKey}]`);
        }
        setSelectedIndividualEquipo(detalleConResultado.idEquipo.toString());
        return;
      }

      // 2. SEGUNDA PRIORIDAD: Si no hay resultados, buscar primer equipo real (no "ALL" ni "SIN_EQUIPO")
      const equiposUnicosEnGrupo = [...new Set(group.map(d => d.idEquipo).filter(Boolean))];
      if (DEBUG_PERFORMANCE) {
        console.log(`🔍 [PERF-DEBUG] Equipos únicos en grupo [${groupKey}]:`, equiposUnicosEnGrupo);
      }

      if (equiposUnicosEnGrupo.length >= 1) {
        // Preferir cualquier equipo real sobre "SIN_EQUIPO"
        const primerEquipoReal = equiposUnicosEnGrupo[0];
        if (DEBUG_PERFORMANCE) {
          console.log(`🎯 [PERF-DEBUG] Seleccionando primer equipo real disponible: ${primerEquipoReal} para [${groupKey}]`);
        }
        setSelectedIndividualEquipo(primerEquipoReal.toString());
        return;
      }

      // 3. TERCERA PRIORIDAD: Si solo hay analitos sin equipo, verificar esa condición
      const todosSinEquipo = group.every(detalle => !detalle.idEquipo);
      if (todosSinEquipo) {
        if (DEBUG_PERFORMANCE) {
          console.log(`🚫 [PERF-DEBUG] Todos los analitos sin equipo en [${groupKey}], seleccionando SIN_EQUIPO`);
        }
        setSelectedIndividualEquipo("SIN_EQUIPO");
        return;
      }

      if (DEBUG_PERFORMANCE) {
        console.log(`📋 [PERF-DEBUG] No se cumplieron condiciones de preselección para [${groupKey}] - manteniendo ALL`);
      }
    }
  }, [groupKey, group, selectedIndividualEquipo, manuallySelected]); // Remover selectedTopEquipo de las dependencias

  // Sincronización con filtro global - logs solo para debugging específico
  useEffect(() => {
    // Solo loggear si estamos debuggeando performance
    if (DEBUG_PERFORMANCE) {
      console.log(`🔄 [PERF-DEBUG] UseEffect sincronización global [${groupKey}] - Equipo Global: ${selectedTopEquipo}, Equipo Local: ${selectedEquipo}`);
    }

    // Solo sincronizar si:
    // 1. La selección no fue manual
    // 2. El equipo global cambió Y no es "ALL"
    // 3. El equipo global existe en las opciones de este grupo
    if (!manuallySelected && selectedTopEquipo !== "ALL") {
      // Verificar si el equipo global existe en las opciones de este grupo
      const equipoExisteEnGrupo = equipoFilterOptions.some(option => 
        option.value.toString() === selectedTopEquipo.toString()
      );

      if (equipoExisteEnGrupo) {
        if (DEBUG_PERFORMANCE) {
          console.log(`✅ [PERF-DEBUG] Sincronizando grupo [${groupKey}] con equipo global ${selectedTopEquipo} (existe en opciones)`);
        }
        setSelectedIndividualEquipo(selectedTopEquipo.toString());
      } else {
        if (DEBUG_PERFORMANCE) {
          console.log(`⚠️ [PERF-DEBUG] Equipo global ${selectedTopEquipo} no existe en grupo [${groupKey}], manteniendo selección actual: ${selectedIndividualEquipo}`);
        }
        // NO HACER NADA - mantener la selección actual
      }
    } else if (selectedTopEquipo === "ALL" && !manuallySelected) {
      // CAMBIO: NO resetear automáticamente cuando el filtro global es "ALL"
      // Permitir que cada grupo mantenga su selección individual
      if (DEBUG_PERFORMANCE) {
        console.log(`🔄 [PERF-DEBUG] Filtro global en ALL, manteniendo selección individual de grupo [${groupKey}]: ${selectedIndividualEquipo}`);
      }
    }
  }, [selectedTopEquipo, equipoFilterOptions, groupKey, manuallySelected, selectedIndividualEquipo]);

  // Reset del filtro individual cuando cambia el grupo o cuando se monta el componente
  useEffect(() => {
    // Solo loggear si estamos debuggeando performance
    if (DEBUG_PERFORMANCE) {
      console.log(`🔄 [PERF-DEBUG] UseEffect reset filtro [${groupKey}] - Equipo Global: ${selectedTopEquipo}, Equipo Local: ${selectedEquipo}`);
    }

    // Solo sincronizar si:
    // 1. La selección no fue manual
    // 2. El equipo global cambió
    // 3. El equipo global existe en las opciones de este grupo
    if (!manuallySelected && selectedTopEquipo !== "ALL") {
      // Verificar si el equipo global existe en las opciones de este grupo
      const equipoExisteEnGrupo = equipoFilterOptions.some(option => 
        option.value.toString() === selectedTopEquipo.toString()
      );

      if (equipoExisteEnGrupo) {
        if (DEBUG_PERFORMANCE) {
          console.log(`✅ [PERF-DEBUG] Sincronizando grupo [${groupKey}] con equipo global ${selectedTopEquipo} (existe en opciones)`);
        }
        setSelectedIndividualEquipo(selectedTopEquipo.toString());
      } else {
        if (DEBUG_PERFORMANCE) {
          console.log(`⚠️ [PERF-DEBUG] Equipo global ${selectedTopEquipo} no existe en grupo [${groupKey}], manteniendo selección actual: ${selectedIndividualEquipo}`);
        }
        // NO HACER NADA - mantener la selección actual
      }
    } else if (selectedTopEquipo === "ALL" && !manuallySelected) {
      // Solo resetear a "ALL" cuando el filtro global se resetea explícitamente
      if (DEBUG_PERFORMANCE) {
        console.log(`🔄 [PERF-DEBUG] Reseteando filtro individual de grupo [${groupKey}] a ALL`);
      }
      setSelectedIndividualEquipo("");
    }
  }, [selectedTopEquipo, equipoFilterOptions, groupKey, manuallySelected, selectedIndividualEquipo]);

  // Manejador para cambio de equipo individual - sin logs innecesarios
  const handleIndividualEquipoChange = (newValue) => {
    // Solo loggear si estamos debuggeando performance
    if (DEBUG_PERFORMANCE) {
      console.log(`🔄 [PERF-DEBUG] Cambio equipo individual [${groupKey}]:`, newValue, "(selección manual)");
    }
    setSelectedIndividualEquipo(newValue || "");
    setManuallySelected(true); // Marcar como selección manual
  };

  // Al hacer clic en el ícono, delegamos al padre el toggle de visibilidad:
  const handleToggleComentario = (id) => {
    toggleComentarioVisible(id);
  };

  // Función para manejar la subida de documentos
  const handleFileUpload = async (detalle, file) => {
    if (!file) return;

    // Validar tipo de archivo (solo PDF)
    if (file.type !== 'application/pdf') {
      alert('Solo se permiten archivos PDF');
      return;
    }

    // Validar tamaño del archivo (máximo 10MB)
    const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
    if (file.size > MAX_FILE_SIZE) {
      alert('El archivo es demasiado grande. El tamaño máximo permitido es de 10 MB.');
      return;
    }

    // Validar que tengamos los datos necesarios
    if (!detalle.idDetalle) {
      alert('Error: ID del detalle no disponible');
      return;
    }

    if (!user?.codigoUsuario) {
      alert('Error: Usuario no disponible');
      return;
    }

    console.log('Preparando subida para detalle:', {
      idDetalle: detalle.idDetalle,
      usuario: user.codigoUsuario,
      fileName: file.name,
      fileSize: file.size,
      fileType: file.type
    });

    try {
      if (onDocumentUpload) {
        await onDocumentUpload(detalle, file);
      }
    } catch (error) {
      console.error('Error al subir documento:', error);
      alert('Error al subir el documento');
    }
  };

  // Función para manejar la eliminación de documentos
  const handleFileDelete = async (detalle) => {
    try {
      if (onDocumentDelete) {
        await onDocumentDelete(detalle);
      }
    } catch (error) {
      console.error('Error al eliminar documento:', error);
      alert('Error al eliminar el documento');
    }
  };

  return (
    <div key={groupKey} style={{ marginBottom: '30px' }}>
      <Grid container spacing={1} alignItems="center" sx={{ mb: 1 }}>
        <Grid item xs={12}>
          <Typography variant="h6">{groupKey}</Typography>
        </Grid>

        
        {!esRemitido && (
          <>
            <Grid item xs={12} md={6}>
              <Autocomplete
                size="small"
                options={equipoFilterOptions}
                getOptionLabel={(option) => option.label}
                value={equipoFilterOptions.find(opt => opt.value.toString() === selectedIndividualEquipo.toString()) || null}
                isOptionEqualToValue={(option, value) => option.value.toString() === value.value.toString()}
                onChange={(e, newValue) => handleIndividualEquipoChange(newValue ? newValue.value : "ALL")}
                disablePortal
                componentsProps={{
                  popper: {
                    tabIndex: -1
                  }
                }}
                renderInput={(params) => (
                  <TextField 
                    {...params} 
                    label={`Equipo Individual${selectedIndividualEquipo !== "" && selectedIndividualEquipo !== "ALL" ? ` (Seleccionado: ${equipoFilterOptions.find(opt => opt.value.toString() === selectedIndividualEquipo.toString())?.label || selectedIndividualEquipo})` : ""}`}
                    variant="outlined" 
                    fullWidth 
                    tabIndex={-1}
                    inputProps={{
                      ...params.inputProps,
                      tabIndex: -1
                    }}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Autocomplete
                size="small"
                options={metodoFilterOptions}
                getOptionLabel={(option) => option.label || ''}
                value={metodoFilterOptions.find(opt => opt.value.toString() === selectedMetodo.toString()) || null}
                isOptionEqualToValue={(option, value) => option.value.toString() === value.value.toString()}
                onChange={(e, newValue) => {
                  const newMetodoValue = newValue ? newValue.value : "";
                  // Solo loggear si estamos debuggeando performance
                  if (DEBUG_PERFORMANCE) {
                    console.log(`🔄 [PERF-DEBUG] Método cambiado en grupo ${groupKey}:`, newMetodoValue === "" ? "VACÍO" : newMetodoValue);
                  }
                  if (onMetodoChange) {
                    onMetodoChange(newMetodoValue);
                  }
                }}
                disablePortal
                componentsProps={{
                  popper: {
                    tabIndex: -1
                  }
                }}
                renderInput={(params) => (
                  <TextField 
                    {...params} 
                    label={`Método${selectedMetodo !== "" && selectedMetodo !== "ALL" ? ` (Seleccionado: ${metodoFilterOptions.find(opt => opt.value.toString() === selectedMetodo.toString())?.label || selectedMetodo})` : ""}`}
                    fullWidth 
                    tabIndex={-1}
                    inputProps={{
                      ...params.inputProps,
                      tabIndex: -1
                    }}
                  />
                )}
                clearIcon={selectedMetodo !== "" ? undefined : null}
                disableClearable={false}
              />
            </Grid>
          </>
        )}
      </Grid>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Analito</TableCell>
              {!esRemitido && <TableCell>Resultado / Equipo</TableCell>}
              {!esRemitido && <TableCell>Valor Referencia / Sexo y Edad</TableCell>}
              {!esRemitido && <TableCell>Comentario</TableCell>}
            </TableRow>
          </TableHead>

            <TableBody>
              {filteredGroup.map((detalle) => (
                <TableRow key={detalle.analitoUniqueId || `${detalle.idTipoAnalisis}-${detalle.idMuestra}-${detalle.idAnalito}-${detalle.idEquipo || 'sin_equipo'}`}>
                  <TableCell>
                    {detalle.analito}
                    {detalle.analitoUniqueId && (
                      <Typography variant="caption" display="block" color="textSecondary">
                        ID: {detalle.id}
                      </Typography>
                    )}
                    {esRemitido && (
                      <Typography variant="caption" display="block" color="warning.main" sx={{ fontWeight: 'bold' }}>
                        🏥 Análisis remitido - Solo requiere PDF
                      </Typography>
                    )}
                  </TableCell>

                  {/* ========== COLUMNA "Resultado / Equipo" ========== */}
                  {!esRemitido && (
                    <TableCell>
                      <Grid container spacing={1}>
                        <Grid item xs={12}>

                          
                          <Autocomplete
                            freeSolo
                            size="small"
                            options={
                              Array.isArray(detalle.resultadoPredeterminado)
                                ? detalle.resultadoPredeterminado
                                : (detalle.resultadoPredeterminado?.$values || [])
                            }
                            getOptionLabel={(option) =>
                              typeof option === 'string'
                                ? option
                                : option.resultadoPredeterminado || ''
                            }
                            //Se cambia esta logica de getLocalValue para corregir el error de resultado al intentar borrar
                            /* value={
                              getLocalValue(`resultado-${detalle.idMuestra}-${detalle.id}`) || 
                              (typeof detalle.resultado === 'string' ? detalle.resultado : (detalle.resultado?.resultadoPredeterminado || ''))
                              
                            }*/
                            //Por esta logica:
                            value={
                                localValues[`resultado-${detalle.idMuestra}-${detalle.id}`] ??
                                localResultados[`resultado-${detalle.idMuestra}-${detalle.id}`] ??
                                (
                                  typeof detalle.resultado === 'string'
                                    ? detalle.resultado
                                    : (detalle.resultado?.resultadoPredeterminado ?? '')
                                )
                              }

                            onOpen={() => {
                              // Marcar el autocomplete como abierto
                              const fieldId = `resultado-${detalle.idMuestra}-${detalle.id}`;
                              autocompleteOpenState.current[fieldId] = true;
                              if (DEBUG_PERFORMANCE) {
                                console.log(`🎯 [${groupKey}] Autocomplete ABIERTO: ${fieldId}`);
                              }
                            }}
                            onClose={() => {
                              // Marcar el autocomplete como cerrado
                              const fieldId = `resultado-${detalle.idMuestra}-${detalle.id}`;
                              autocompleteOpenState.current[fieldId] = false;
                              if (DEBUG_PERFORMANCE) {
                                console.log(`🎯 [${groupKey}] Autocomplete CERRADO: ${fieldId}`);
                              }
                            }}
                            onChange={(e, newValue) => {
                              let valorFinal = '';
                              if (typeof newValue === 'string') {
                                valorFinal = newValue;
                              } else if (newValue && typeof newValue === 'object') {
                                valorFinal = newValue.resultadoPredeterminado || newValue.toString();
                              }
                              const key = `resultado-${detalle.idMuestra}-${detalle.id}`;
                              handleLocalChange(key, valorFinal);
                            }}
                            onInputChange={(e, newInputValue) => {
                              if (typeof newInputValue === 'string') {
                                const key = `resultado-${detalle.idMuestra}-${detalle.id}`;
                                handleLocalChange(key, newInputValue);
                              }
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Resultado"
                                variant="outlined"
                                fullWidth
                                size="small"
                                title="💡 Para formato párrafo en el reporte: coloca ** al inicio y ** al final del texto (ej: **mi resultado**)"
                                inputRef={(el) => {
                                  // Guardar referencia para navegación con teclado
                                  const fieldId = `resultado-${detalle.idMuestra}-${detalle.id}`;
                                  if (el) {
                                    // Registro local (para navegación dentro del grupo)
                                    inputRefs.current[fieldId] = el;
                                    // 🎯 Registro global (para navegación entre grupos)
                                    if (globalInputRefs?.current) {
                                      globalInputRefs.current[fieldId] = el;
                                      if (DEBUG_PERFORMANCE) {
                                        console.log(`🎯 [${groupKey}] Registrando campo global: ${fieldId}`);
                                      }
                                    }
                                    if (DEBUG_PERFORMANCE) {
                                      console.log(`🎯 [${groupKey}] Registrando campo local: ${fieldId}`);
                                    }
                                  }
                                }}
                                onKeyDown={(e) => {
                                  const fieldId = `resultado-${detalle.idMuestra}-${detalle.id}`;
                                  
                                  // SOLUCIÓN DEFINITIVA: Para Tab/Shift+Tab, forzar que el autocomplete esté cerrado
                                  let isOpen = autocompleteOpenState.current[fieldId] === true;
                                  
                                  if (e.key === 'Tab') {
                                    // Para navegación Tab, forzamos que esté cerrado para usar navegación global
                                    autocompleteOpenState.current[fieldId] = false;
                                    isOpen = false;
                                    
                                    if (DEBUG_PERFORMANCE) {
                                      console.log(`🎯 [${groupKey}] Tab detectado - FORZANDO cierre del autocomplete para ${fieldId}`);
                                    }
                                  }
                                  
                                  if (DEBUG_PERFORMANCE) {
                                    console.log(`🎯 [${groupKey}] KeyDown DEFINITIVO en ${fieldId}:`);
                                    console.log(`   - Tecla: ${e.key}`);
                                    console.log(`   - Estado original: ${autocompleteOpenState.current[fieldId]}`);
                                    console.log(`   - isOpen final: ${isOpen}`);
                                  }
                                  
                                  //handleKeyDown(e, fieldId, isOpen);

                                    //Solucionando problema de navegacion de Resultado/Equipo para Analitos, cuando solo tiene 1 resultado
                                    const handleKeyDown = (e, fieldId, isOpen) => {
                                      // Si el Autocomplete está abierto, dejamos que él maneje las teclas
                                      if (isOpen) return;

                                      // Nos interesan estas teclas para navegación
                                      if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp' && e.key !== 'Tab') {
                                        return;
                                      }

                                      if (!globalInputRefs?.current) return;

                                      // Obtenemos todos los ids registrados (por ejemplo, todos los "resultado-...")
                                      const ids = Object.keys(globalInputRefs.current)
                                        .filter((id) => id.startsWith("resultado-")) // opcional pero ayuda
                                        .sort(); // si tienes un orden custom, aplícalo aquí

                                      const total = ids.length;

                                      // Si solo hay 1 campo, no forzamos navegación custom
                                      if (total <= 1) {
                                        return; // deja el comportamiento normal del navegador
                                      }

                                      const currentIndex = ids.indexOf(fieldId);
                                      if (currentIndex === -1) {
                                        return;
                                      }

                                      let nextIndex = null;

                                      // Flechas arriba/abajo
                                      if (e.key === "ArrowDown") {
                                        nextIndex = currentIndex + 1;
                                      } else if (e.key === "ArrowUp") {
                                        nextIndex = currentIndex - 1;
                                      }

                                      // Tab / Shift+Tab
                                      if (e.key === "Tab") {
                                        if (e.shiftKey) {
                                          nextIndex = currentIndex - 1;
                                        } else {
                                          nextIndex = currentIndex + 1;
                                        }
                                      }

                                      // Si el índice calculado está fuera de rango, no hacemos nada especial
                                      if (nextIndex == null || nextIndex < 0 || nextIndex >= total) {
                                        return; // no hacemos preventDefault, dejamos que el navegador haga lo suyo
                                      }

                                      const nextId = ids[nextIndex];
                                      const nextElement = globalInputRefs.current[nextId];

                                      if (nextElement) {
                                        e.preventDefault(); // aquí SÍ bloqueamos el comportamiento normal
                                        nextElement.focus();
                                      }
                                   };
                                }}
                                sx={{
                                  "& .MuiOutlinedInput-root": {
                                    border:
                                      detalle.resultado &&
                                        detalle.resultado.toString().trim() !== "" &&
                                        detalle.idEquipo
                                        ? `1px solid #2196f3`
                                        : "inherit",
                                  }
                                }}
                              />
                            )}
                          />
                        </Grid>
                      </Grid>
                    </TableCell>
                  )}

                  {/* ========== COLUMNA "Valor Referencia / Sexo y Edad" ========== */}
                  {!esRemitido && (
                    <TableCell>
                      <Grid container direction="column">
                        <Grid item>
                          <Typography variant="body2">
                            <strong>{detalle.valorReferencia}</strong>
                            {detalle.descripcionUnidadDeMedida && ` ${detalle.descripcionUnidadDeMedida}`}
                          </Typography>
                        </Grid>
                        <Grid item>
                          <Typography variant="caption" color="textSecondary">
                            {detalle.idSexo === 1 && "Masculino, "}
                            {detalle.idSexo === 2 && "Femenino, "}
                            {detalle.idSexo === 3 && "Ambos, "}
                            {detalle.edadMinima !== undefined && detalle.edadMaxima !== undefined &&
                              `${detalle.edadMinima}-${detalle.edadMaxima} días`}
                          </Typography>
                        </Grid>
                        <Grid item>
                          <Typography variant="caption" color="primary">
                            {detalle.idEquipo 
                              ? (equipoOptions.find(eq =>
                                  eq.id.toString() === detalle.idEquipo.toString()
                                )?.descripcion || `ID: ${detalle.idEquipo}`)
                              : "Sin equipo"
                            }
                          </Typography>
                        </Grid>
                      </Grid>
                    </TableCell>
                  )}

                  {/* ========== COLUMNA "Comentario" ========== */}
                  {!esRemitido && (
                    <TableCell>
                      <Grid container spacing={1} alignItems="center">
                        <Grid item>
                          <CommentIcon
                            color={comentarioVisible[detalle.id] ? "primary" : "disabled"}
                            onClick={() => handleToggleComentario(detalle.id)}
                            style={{ cursor: "pointer" }}
                          />
                        </Grid>

                        {comentarioVisible[detalle.id] && (
                          <Grid item xs={12}>
                            <Autocomplete
                              freeSolo
                              size="small"
                              options={
                                Array.isArray(detalle.comentariosPredeterminados)
                                  ? detalle.comentariosPredeterminados
                                  : (detalle.comentariosPredeterminados?.$values || [])
                              }
                              getOptionLabel={(option) =>
                                typeof option === 'string'
                                  ? option
                                  : option.comentarioPredeterminado || ''
                              }
                              value={
                                getLocalValue(`comentario-${detalle.idMuestra}-${detalle.id}`) || 
                                (typeof detalle.comentario === 'string' ? detalle.comentario : (detalle.comentario?.comentarioPredeterminado || ''))
                              }
                              onChange={(e, newValue) => {
                                let valorFinal = '';
                                if (typeof newValue === 'string') {
                                  valorFinal = newValue;
                                } else if (newValue && typeof newValue === 'object') {
                                  valorFinal = newValue.comentarioPredeterminado || newValue.toString();
                                }
                                const key = `comentario-${detalle.idMuestra}-${detalle.id}`;
                                handleLocalChange(key, valorFinal);
                              }}
                              onInputChange={(e, newInputValue) => {
                                if (typeof newInputValue === 'string') {
                                  const key = `comentario-${detalle.idMuestra}-${detalle.id}`;
                                  handleLocalChange(key, newInputValue);
                                }
                              }}
                              disablePortal
                              componentsProps={{
                                popper: {
                                  tabIndex: -1
                                }
                              }}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  label="Comentario"
                                  variant="outlined"
                                  fullWidth
                                  size="small"
                                  tabIndex={-1}
                                  inputProps={{
                                    ...params.inputProps,
                                    tabIndex: -1
                                  }}
                                />
                              )}
                            />
                          </Grid>
                        )}
                      </Grid>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

      {/* ========== Interfaz centrada en PDF para análisis remitidos ========== */}
      {esRemitido ? (
        <Box sx={{ mt: 2, p: 3, border: '2px dashed #2196f3', borderRadius: 2, backgroundColor: '#f8f9ff' }}>
          <Grid container spacing={3} alignItems="center" justifyContent="center">
            {/* Título y descripción central */}
            <Grid item xs={12} textAlign="center">
              <Typography variant="h6" color="primary" sx={{ mb: 1, fontWeight: 'bold' }}>
                📄 Análisis Remitido - Subir Documento PDF
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Este análisis se realiza en laboratorio externo. Adjunte el resultado en formato PDF.
              </Typography>
            </Grid>

            {/* Área central de subida de archivo */}
            <Grid item xs={12} md={8}>
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center', 
                gap: 2, 
                p: 3, 
                border: '1px solid #e0e0e0', 
                borderRadius: 2, 
                backgroundColor: 'white',
                minHeight: '120px',
                justifyContent: 'center'
              }}>
                {/* Botones de acción principales */}
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                  {/* Botón principal de subir */}
                  <input
                    accept="application/pdf"
                    style={{ display: 'none' }}
                    id={`file-upload-group-${groupKey}`}
                    type="file"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file && group.length > 0) {
                        handleFileUpload(group[0], file);
                      }
                    }}
                  />
                  <label htmlFor={`file-upload-group-${groupKey}`}>
                    <Button
                      component="span"
                      variant="contained"
                      color="primary"
                      size="large"
                      startIcon={<CloudUploadIcon />}
                      sx={{ px: 4, py: 1.5, fontSize: '1.1rem' }}
                    >
                      {group[0]?.urlResultadoRemitido ? 'Cambiar PDF' : 'Subir PDF'}
                    </Button>
                  </label>

                  {/* Botones secundarios */}
                  {group[0]?.urlResultadoRemitido && (
                    <>
                      <IconButton
                        size="large"
                        color="success"
                        title="Ver documento PDF"
                        onClick={() => window.open(group[0].urlResultadoRemitido, '_blank')}
                        sx={{ 
                          border: '2px solid', 
                          borderColor: 'success.main',
                          '&:hover': { backgroundColor: 'success.light', color: 'white' }
                        }}
                      >
                        <VisibilityIcon />
                      </IconButton>

                      <IconButton
                        size="large"
                        color="error"
                        title="Eliminar documento PDF"
                        onClick={() => handleFileDelete(group[0])}
                        sx={{ 
                          border: '2px solid', 
                          borderColor: 'error.main',
                          '&:hover': { backgroundColor: 'error.light', color: 'white' }
                        }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </>
                  )}
                </Box>

                {/* Indicador de estado prominente */}
                <Box sx={{ textAlign: 'center' }}>
                  {group[0]?.urlResultadoRemitido ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'center' }}>
                      <Typography variant="h6" color="success.main" sx={{ fontWeight: 'bold' }}>
                        ✅ Documento PDF adjunto
                      </Typography>
                    </Box>
                  ) : (
                    <Typography variant="body1" color="warning.main" sx={{ fontWeight: 'bold' }}>
                      ⚠️ Sin documento PDF - Requerido para completar
                    </Typography>
                  )}
                </Box>

                {/* Información adicional */}
                <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center', maxWidth: '400px' }}>
                  Formatos aceptados: PDF únicamente • Tamaño máximo recomendado: 10MB
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Box>
      ) : (
        /* ========== Interfaz normal para análisis no remitidos ========== */
        <Box sx={{ mt: 2 }}>
          <Grid container spacing={2} alignItems="flex-start">
            <Grid item xs={12} md={12}>
              <TextField
                size="small"
                label="Comentario del Resultado"
                multiline
                fullWidth
                variant="outlined"
                value={comentariosResultado[groupKey] || ""}
                onChange={(e) => handleComentarioResultadoChange(groupKey, e.target.value)}
                tabIndex={-1}
                inputProps={{
                  tabIndex: -1
                }}
              />
            </Grid>
          </Grid>
        </Box>
      )}
    </div>
  );
};

export default memo(GrupoAnalitos, (prevProps, nextProps) => {
  // Comparación optimizada para evitar re-renderizaciones innecesarias
  // Solo re-renderizar si las props específicas de este grupo cambiaron
  
  // Si el groupKey cambió, definitivamente re-renderizar
  if (prevProps.groupKey !== nextProps.groupKey) {
    return false;
  }
  
  // Si el group array cambió (diferente referencia o contenido), re-renderizar
  if (prevProps.group !== nextProps.group || prevProps.group.length !== nextProps.group.length) {
    return false;
  }
  
  // Verificar si algún analito en el grupo cambió su resultado o comentario
  for (let i = 0; i < prevProps.group.length; i++) {
    const prevDetalle = prevProps.group[i];
    const nextDetalle = nextProps.group[i];
    
    if (prevDetalle.resultado !== nextDetalle.resultado || 
        prevDetalle.comentario !== nextDetalle.comentario ||
        prevDetalle.idEquipo !== nextDetalle.idEquipo) {
      return false;
    }
  }
  
  // Si las opciones de equipo cambiaron significativamente, re-renderizar
  if (prevProps.equipoOptions.length !== nextProps.equipoOptions.length) {
    return false;
  }
  
  // Si los comentarios de resultado para este grupo específico cambiaron, re-renderizar
  if (prevProps.comentariosResultado[prevProps.groupKey] !== nextProps.comentariosResultado[nextProps.groupKey]) {
    return false;
  }
  
  // Si la visibilidad de comentarios cambió para algún analito de este grupo, re-renderizar
  const groupIds = prevProps.group.map(d => d.id);
  for (const id of groupIds) {
    if (prevProps.comentarioVisible[id] !== nextProps.comentarioVisible[id]) {
      return false;
    }
  }
  
  // Si el equipo seleccionado para este grupo cambió, re-renderizar
  if (prevProps.selectedEquipo !== nextProps.selectedEquipo) {
    return false;
  }
  
  // Si el método seleccionado para este grupo cambió, re-renderizar
  if (prevProps.selectedMetodo !== nextProps.selectedMetodo) {
    return false;
  }
  
  // Si el filtro global top cambió, re-renderizar (afecta la sincronización)
  if (prevProps.selectedTopEquipo !== nextProps.selectedTopEquipo) {
    return false;
  }
  
  // Optimización crítica: Solo re-renderizar si los valores locales de este grupo específico cambiaron
  if (prevProps.localResultados !== nextProps.localResultados || 
      prevProps.localComentarios !== nextProps.localComentarios) {
    // Verificar cambios específicos para analitos de este grupo solamente
    const groupIds = prevProps.group.map(d => `${d.idMuestra}-${d.id}`);
    const hasLocalChanges = groupIds.some(id => {
      const resultadoKey = `resultado-${id}`;
      const comentarioKey = `comentario-${id}`;
      return prevProps.localResultados?.[resultadoKey] !== nextProps.localResultados?.[resultadoKey] ||
             prevProps.localComentarios?.[comentarioKey] !== nextProps.localComentarios?.[comentarioKey];
    });
    
    if (hasLocalChanges) {
      return false; // Re-renderizar solo si este grupo específico tiene cambios
    }
  }
  
  // Si llegamos aquí, no hay cambios relevantes - NO re-renderizar
  return true;
});
